import { api } from '@/lib/api'
import type {
  PesquisaClima,
  RedeCanal,
  RedeEvento,
  RedePost,
  RedeRankingItem,
  TvCampanha,
  TvMedia,
  TvPlaylist,
  WhatsappContato,
  WhatsappStatus,
  WhatsappTemplate,
} from './types'

/** Normaliza listagens DRF / hi Conecta (`dados` | `results` | array). */
export function unwrapList<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[]
  if (res && typeof res === 'object') {
    const o = res as Record<string, unknown>
    if (Array.isArray(o.dados)) return o.dados as T[]
    if (Array.isArray(o.results)) return o.results as T[]
  }
  return []
}

// ── Rede Corporativa ─────────────────────────────────────────

export async function fetchRedeFeed(opts: { tipo?: string; page?: number } = {}) {
  const qs = new URLSearchParams()
  if (opts.tipo) qs.set('tipo', opts.tipo)
  if (opts.page) qs.set('page', String(opts.page))
  const suffix = qs.toString() ? `?${qs}` : ''
  const res = await api.get<{ results?: RedePost[]; page?: number }>(`/rede/feed/${suffix}`)
  return { posts: res.results || [], page: res.page || 1 }
}

export async function criarRedePost(payload: {
  conteudo: string
  titulo?: string
  tipo?: string
}) {
  return api.post<RedePost>('/rede/feed/', payload)
}

export async function reagirRedePost(postId: string, tipo = 'curtir') {
  return api.post<{ ok?: boolean }>(`/rede/feed/${postId}/reagir/`, { tipo })
}

export async function comentarRedePost(postId: string, conteudo: string) {
  return api.post(`/rede/feed/${postId}/comentar/`, { conteudo })
}

export async function fetchRedeRanking(top = 10) {
  const res = await api.get<{ results?: RedeRankingItem[] }>(`/rede/ranking/?top=${top}`)
  return res.results || []
}

export async function fetchRedeEventos() {
  const res = await api.get<{ results?: RedeEvento[] }>('/rede/eventos/')
  return res.results || []
}

export async function fetchRedeCanais() {
  const res = await api.get<{ results?: RedeCanal[] }>('/rede/canais/')
  return res.results || []
}

export async function fetchHeatmapEmocional(dias = 30) {
  return api.get<{ media_bem_estar?: number; dias?: number }>(
    `/rede/emocional/heatmap/?dias=${dias}`,
  )
}

export async function checkinEmocional(payload: {
  emocao: string
  contexto?: string
  intensidade?: number
  comentario?: string
  anonimo?: boolean
}) {
  return api.post('/rede/emocional/checkin/', payload)
}

// ── TV Corporativa ───────────────────────────────────────────

export async function fetchTvMidiasAtivas() {
  const res = await api.get<unknown>('/endomarketing/tv/media/ativos/')
  return unwrapList<TvMedia>(res)
}

export async function fetchTvMidias(params: { status?: string; categoria?: string } = {}) {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.categoria) qs.set('categoria', params.categoria)
  const suffix = qs.toString() ? `?${qs}` : ''
  const res = await api.get<unknown>(`/endomarketing/tv/media/${suffix}`)
  return unwrapList<TvMedia>(res)
}

export async function fetchTvPlaylists() {
  const res = await api.get<unknown>('/endomarketing/tv/playlists/')
  return unwrapList<TvPlaylist>(res)
}

export async function fetchTvCampanhas(ativas = false) {
  const qs = ativas ? '?ativas=1' : ''
  const res = await api.get<unknown>(`/endomarketing/tv/campanhas/${qs}`)
  return unwrapList<TvCampanha>(res)
}

export async function fetchTvAnalyticsResumo() {
  return api.get<{
    total_visualizacoes?: number
    total_midias?: number
    top_midias?: TvMedia[]
    por_categoria?: Record<string, number>
    por_tipo?: Record<string, number>
  }>('/endomarketing/tv/media/analytics-resumo/')
}

export async function registrarTvView(id: string | number) {
  return api.post(`/endomarketing/tv/media/${id}/visualizar/`, {})
}

// ── Clima ────────────────────────────────────────────────────

export async function fetchClimaPesquisas(status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return api.get<{
    sucesso?: boolean
    dados: PesquisaClima[]
    total?: number
    ativas?: number
  }>(`/rh/clima/pesquisas/${qs}`)
}

export async function responderPesquisaClima(id: string, respostas: Record<string, unknown>) {
  return api.post<{ sucesso?: boolean; total_respostas?: number }>(
    `/rh/clima/pesquisas/${id}/responder/`,
    { respostas },
  )
}

// ── WhatsApp ─────────────────────────────────────────────────

export async function fetchWhatsappStatus() {
  const res = await api.get<{ sucesso?: boolean; dados: WhatsappStatus }>(
    '/comunicacao/whatsapp/status/',
  )
  return res.dados
}

export async function fetchWhatsappInbox() {
  return api.get<{
    sucesso?: boolean
    dados: WhatsappContato[]
    total?: number
    nao_lidas?: number
    status?: string
  }>('/comunicacao/whatsapp/inbox/')
}

export async function fetchWhatsappTemplates() {
  const res = await api.get<{ sucesso?: boolean; dados: WhatsappTemplate[] }>(
    '/comunicacao/whatsapp/templates/',
  )
  return res.dados || []
}
